import {
  forwardRef,
  type MouseEventHandler,
  type ReactNode,
  type Ref,
} from 'react';
import { Dimension } from '../Dimension/Dimension';
import styles from './IconButton.module.css';

export type StyleVariant =
  | 'muted'
  | 'inverted'
  | 'default'
  | 'success'
  | 'warning'
  | 'critical'
  | 'disabled';

export type ProminenceVariant = 'primary' | 'secondary' | 'tertiary';

export type SizeVariant = 'small' | 'medium' | 'large';

export type OutlineVariant = 'outline' | 'shadow';

interface LinkButtonProps {
  buttonType: 'link';
  to: string;
  openLinkInNewTab?: boolean;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
}

interface SubmitButtonProps {
  buttonType?: 'submit' | 'button';
  onClick?: MouseEventHandler<HTMLButtonElement>;
}

interface VisualButtonProps {
  buttonType: 'visual';
}

export type Props = {
  testid?: string;
  style: StyleVariant;
  prominence: ProminenceVariant;
  size: SizeVariant;
  className?: string;
  icon: ReactNode;
  outline?: OutlineVariant;
  ariaLabel: string;
} & (LinkButtonProps | SubmitButtonProps | VisualButtonProps);

function getVariantClass(
  style: Props['style'],
  prominence: Props['prominence'],
  size: Props['size'],
  outline: Props['outline']
) {
  return [
    styles[`${style}-${prominence}`],
    styles[`${size}`],
    styles[`${outline}`],
    prominence === 'secondary' && outline && styles['secondary-shadow'],
  ].join(' ');
}

function getDimensionLevel(
  outline: Props['outline'],
  prominence: Props['prominence'],
  style: Props['style']
) {
  if (
    (outline && prominence === 'secondary') ||
    (prominence === 'primary' && style === 'inverted')
  ) {
    return 'two';
  }
  return 'none';
}

export const IconButton = forwardRef(
  (props: Props, ref: Ref<HTMLButtonElement | HTMLAnchorElement>) => {
    const {
      testid,
      style,
      prominence,
      size,
      className,
      icon,
      outline,
      ariaLabel,
    } = props;

    const buttonIsDisabled = style === 'disabled';

    if (props.buttonType === 'link') {
      const { to, openLinkInNewTab, onClick } = props;
      return (
        <Dimension
          level={getDimensionLevel(outline, prominence, style)}
          as="a"
          {...(buttonIsDisabled ? {} : { href: to })}
          target={openLinkInNewTab ? '_blank' : '_self'}
          ref={ref as Ref<HTMLAnchorElement>}
          data-testid={testid}
          className={`${getVariantClass(
            style,
            prominence,
            size,
            outline
          )} ${className}`}
          onClick={buttonIsDisabled ? undefined : onClick}
          aria-label={ariaLabel}
        >
          {icon}
        </Dimension>
      );
    }

    if (props.buttonType === 'visual') {
      return (
        <Dimension
          level={getDimensionLevel(outline, prominence, style)}
          as="span"
          data-testid={testid}
          className={`${getVariantClass(
            style,
            prominence,
            size,
            outline
          )} ${className}`}
          aria-label={ariaLabel}
        >
          {icon}
        </Dimension>
      );
    }

    return (
      <Dimension
        level={getDimensionLevel(outline, prominence, style)}
        as="button"
        ref={ref as Ref<HTMLButtonElement>}
        data-testid={testid}
        className={`${getVariantClass(
          style,
          prominence,
          size,
          outline
        )} ${className}`}
        onClick={buttonIsDisabled ? undefined : props.onClick}
        type={props.buttonType}
        disabled={buttonIsDisabled}
        aria-label={ariaLabel}
      >
        {icon}
      </Dimension>
    );
  }
);
